import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import StepperFormModal from '../../../../shared/components/StepperFormModal';
import CustomWarning from '../../../../shared/components/CustomWarning';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import { StepperNavMode } from '../../../../enums/stepperEnums';
import { QueryKeys } from '../../../../enums/queryKeys';
import { AppRoutes } from '../../../../routes/routeConstants/appRoutes';
import { useAuth } from '../../../../context/AuthContext';
import UserManagerService, {
  buildUserPayload,
  getUserManagerErrorCode,
  getUserManagerErrorMessage,
} from '../../../../services/UserManager/userManager.service';
import { userManagerServiceMessages, ERROR_CODE_NOTIFICATION_TYPE } from '../../../../services/UserManager/userManager.constants';
import { UserFormStep } from '../../constants';
import { UserFormMetaContext } from './userFormMeta';
import type { UserFormValues } from '../../../../types/UserManagementTypes';
import {
  detailsToFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
  USER_FORM_DEFAULTS,
  USER_STEP_FIELDS,
  userFormValidation,
} from './constants';
import { USER_FORM_STEPS } from './steps';
import styles from './UserFormModal.module.scss';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Edit opens on the summary and lets the user jump anywhere; create walks the
   * steps in order.
   */
  isEditing?: boolean;
  /** Row being edited; unused when creating. */
  userId?: number;
  /** Refreshes the list once a user has been created. */
  onCreated?: () => void;
  /**
   * ISO date the client's subscription ends. Caps the access expiry picker;
   * omitted until a subscription source exists, which leaves it uncapped.
   */
  subscriptionEndDate?: string;
}

const CREATE_TITLE = 'Create New User';
const EDIT_TITLE = 'Edit User';
const EXIT_MESSAGE = 'You are about to discard this user creation process. All progress will be lost.';
// Matches the mockup's proportions; 700 stretched the fields too wide.
const MODAL_WIDTH = 600;
const SELF_EMAIL_CHANGE_MESSAGE = 'Your login email has changed — you will be logged out.';
const MULTI_GROUP_PROMPT_MESSAGE = 'Do you want to edit permissions for newly created groups';

const UserFormModal = ({ open, onClose, isEditing = false, userId, onCreated, subscriptionEndDate }: UserFormModalProps) => {
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  const methods = useForm<UserFormValues>({
    defaultValues: USER_FORM_DEFAULTS,
    resolver: yupResolver(userFormValidation) as Resolver<UserFormValues>,
    context: { isEditing },
    // Errors surface when Continue runs the step's gate, not while typing.
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // A failing field's step may not be mounted; focusing its null ref throws and leaves isSubmitting stuck.
    shouldFocusError: false,
  });
  const { trigger, handleSubmit, reset, formState } = methods;

  const queryClient = useQueryClient();
  const { getUserDetails, getRolesAvailability, updateUser, createUser, createGroup } = UserManagerService();
  const { mutateAsync: updateUserAsync } = useMutation({ ...updateUser() });
  const { mutateAsync: createUserAsync } = useMutation({ ...createUser() });
  const { mutateAsync: createGroupAsync } = useMutation({ ...createGroup() });
  const {
    data: userDetails,
    isSuccess: hasUserDetails,
    isError: isUserDetailsError,
    error: userDetailsError,
  } = useQuery({ ...getUserDetails(userId as number), enabled: open && isEditing && !!userId });

  // Same queryKey as the page-level fetch, so it's instant from cache when fresh
  // and auto-revalidates in the background when stale -- no manual invalidation needed.
  const { data: rolesAvailability } = useQuery({ ...getRolesAvailability(), enabled: open });

  const roleAvailability = useMemo(() => Object.fromEntries((rolesAvailability?.roles ?? []).map((role) => [role.name, role])), [rolesAvailability]);

  const formMeta = useMemo(
    () => ({ subscriptionEndDate, isEditing, originalEmail: userDetails?.email, roleAvailability }),
    [subscriptionEndDate, isEditing, userDetails?.email, roleAvailability],
  );

  const [isExitWarningOpen, setIsExitWarningOpen] = useState(false);
  // Holds the validated values while the email-change confirm is up, so
  // confirming resumes the save instead of re-validating from scratch.
  const [pendingEmailChangeValues, setPendingEmailChangeValues] = useState<UserFormValues | null>(null);
  // Names already resolved to a real group id this session, so a retry after a
  // failed create doesn't post the same new group twice.
  const [createdGroupIds, setCreatedGroupIds] = useState<Record<string, number>>({});
  // Outlives the wizard: the prompt is shown after it closes.
  const [pendingGroupNames, setPendingGroupNames] = useState<string[]>([]);

  // Reset on open rather than on close, so a closing modal doesn't visibly
  // empty its own fields mid-animation. Editing waits for the fetched record
  // instead of resetting to blank defaults first.
  useEffect(() => {
    if (!open) return;
    setPendingEmailChangeValues(null);
    if (!isEditing) reset(USER_FORM_DEFAULTS);
    else if (hasUserDetails && userDetails) reset(detailsToFormValues(userDetails));
  }, [open, isEditing, hasUserDetails, userDetails, reset]);

  // The row's user may have been deleted elsewhere between opening the menu and
  // the fetch resolving; there's nothing to edit, so back out instead of
  // leaving the wizard stuck on empty fields.
  useEffect(() => {
    if (!open || !isUserDetailsError) return;
    Notification({
      message: userManagerServiceMessages.USERS.GET_DETAILS_ERROR,
      description: getUserManagerErrorMessage(userDetailsError),
      type: NotificationTypes.ERROR,
    });
    onClose();
  }, [open, isUserDetailsError, userDetailsError, onClose]);

  // Validation gate for Continue; the stepper skips it when going backwards. A
  // step with no required fields passes without running the resolver.
  const steps = useMemo(
    () =>
      USER_FORM_STEPS.map((step) => {
        const fields = USER_STEP_FIELDS[step.id];
        const gated = {
          ...step,
          onBeforeLeave: () => {
            if (formState.isSubmitting) return false;
            return fields.length ? trigger(fields) : true;
          },
        };
        return step.id === UserFormStep.SUMMARY ? { ...gated, nextLabel: isEditing ? 'SAVE' : 'CREATE USER' } : gated;
      }),
    [trigger, isEditing, formState.isSubmitting],
  );

  const handleCloseRequest = useCallback(() => {
    // A create/update request is in flight; closing now would let its success
    // (the "edit permissions?" prompt, a list refresh) land on a wizard the
    // user already dismissed.
    if (formState.isSubmitting) return;
    if (formState.isDirty) {
      setIsExitWarningOpen(true);
      return;
    }
    onClose();
  }, [formState.isSubmitting, formState.isDirty, onClose]);

  const handleExitConfirm = useCallback(() => {
    setIsExitWarningOpen(false);
    onClose();
  }, [onClose]);

  // Creates any brand-new groups first (so the user payload can reference real
  // ids), reusing ids already created this session on a retry rather than
  // posting duplicates.
  const resolveGroupIds = useCallback(
    async (values: UserFormValues): Promise<number[]> => {
      const resolved = { ...createdGroupIds };
      for (const name of values.newGroupNames) {
        if (name in resolved) continue;
        const { id } = await createGroupAsync({ name });
        // Committed as each one succeeds, not after the loop -- so a group
        // created before a later one fails isn't recreated on retry.
        resolved[name] = id;
        setCreatedGroupIds((prev) => ({ ...prev, [name]: id }));
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORG_GROUPS] });
      }
      return [...values.groupIds.map(Number), ...values.newGroupNames.map((name) => resolved[name])];
    },
    [createdGroupIds, createGroupAsync, queryClient],
  );

  const handleCreateSubmit = useCallback(
    async (values: UserFormValues) => {
      try {
        const groupIds = await resolveGroupIds(values);
        const response = await createUserAsync(formValuesToCreatePayload(values, groupIds));
        Notification({
          message: response.message || 'User created successfully',
          type: NotificationTypes.SUCCESS,
        });
        onCreated?.();
        onClose();
        setPendingGroupNames(values.newGroupNames);
      } catch (error) {
        const errorCode = getUserManagerErrorCode(error);
        const notificationType = (errorCode && ERROR_CODE_NOTIFICATION_TYPE[errorCode]) || NotificationTypes.ERROR;
        Notification({
          message: userManagerServiceMessages.USERS.CREATE_ERROR,
          description: getUserManagerErrorMessage(error),
          type: notificationType,
        });
      }
    },
    [resolveGroupIds, createUserAsync, onCreated, onClose],
  );

  const handleEditSubmit = useCallback(
    async (values: UserFormValues) => {
      try {
        const groupIds = await resolveGroupIds(values);
        await updateUserAsync(buildUserPayload(userId as number, formValuesToUpdatePayload(values, groupIds, formState.dirtyFields)));
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_USER_DETAILS, userId] });

        const isSelfEmailChange = String(currentUser?.id) === String(userId) && !!formState.dirtyFields.email;
        if (isSelfEmailChange) {
          Notification({ message: SELF_EMAIL_CHANGE_MESSAGE, description: '', type: NotificationTypes.WARNING });
          logout();
          return;
        }

        Notification({
          message: 'User updated successfully',
          type: NotificationTypes.SUCCESS,
        });
        onCreated?.();
        onClose();
        setPendingGroupNames(values.newGroupNames);
      } catch (error) {
        const errorCode = getUserManagerErrorCode(error);
        const notificationType = (errorCode && ERROR_CODE_NOTIFICATION_TYPE[errorCode]) || NotificationTypes.ERROR;
        Notification({
          message: userManagerServiceMessages.USERS.UPDATE_ERROR,
          description: getUserManagerErrorMessage(error),
          type: notificationType,
        });
      }
    },
    [userId, resolveGroupIds, updateUserAsync, queryClient, currentUser?.id, formState.dirtyFields, logout, onCreated, onClose],
  );

  const handleConfirm = handleSubmit((values) => {
    if (isEditing && formState.dirtyFields.email) {
      setPendingEmailChangeValues(values);
      return;
    }
    return isEditing ? handleEditSubmit(values) : handleCreateSubmit(values);
  });

  const confirmEmailChangeAndSave = useCallback(() => {
    if (!pendingEmailChangeValues) return;
    handleEditSubmit(pendingEmailChangeValues);
    setPendingEmailChangeValues(null);
  }, [pendingEmailChangeValues, handleEditSubmit]);

  const cancelEmailChange = useCallback(() => setPendingEmailChangeValues(null), []);

  // Both actions end the prompt outright rather than advancing to the next
  // pending name -- it now represents "all groups created this submit", not a queue.
  const dismissGroupPrompt = useCallback(() => setPendingGroupNames([]), []);

  const openGroupManager = useCallback(() => {
    setPendingGroupNames([]);
    navigate(AppRoutes.GROUP_MANAGER);
  }, [navigate]);

  return (
    <FormProvider {...methods}>
      <UserFormMetaContext.Provider value={formMeta}>
        <StepperFormModal
          key={`${isEditing}`}
          open={open}
          onClose={handleCloseRequest}
          title={isEditing ? EDIT_TITLE : CREATE_TITLE}
          steps={steps}
          width={MODAL_WIDTH}
          maskClosable
          className={styles.userFormModal}
          initialStep={isEditing ? UserFormStep.SUMMARY : UserFormStep.USER}
          navMode={isEditing ? StepperNavMode.FREE : StepperNavMode.SEQUENTIAL}
          isSubmitting={formState.isSubmitting}
          onConfirm={handleConfirm}
        />
      </UserFormMetaContext.Provider>
      <CustomWarning
        open={isExitWarningOpen}
        onCancel={() => setIsExitWarningOpen(false)}
        onConfirm={handleExitConfirm}
        message={EXIT_MESSAGE}
        cancelText="Back"
        confirmText="Discard"
        variant="dark"
        maskClosable
        maskVisible
      />
      <CustomWarning
        open={!!pendingEmailChangeValues}
        onCancel={cancelEmailChange}
        onConfirm={confirmEmailChangeAndSave}
        cancelText="Back"
        confirmText="CONFIRM"
        variant="dark"
        maskClosable
        maskVisible
        message={
          <>
            THIS USER WILL NO LONGER BE ABLE TO ACCESS MAPAKI FROM THE EMAIL <span className={styles.oldEmail}>{userDetails?.email}</span>.
            <br />
            <br />A NOTIFICATION WILL BE SENT TO <span className={styles.promptGroupName}>{pendingEmailChangeValues?.email}</span>.
          </>
        }
      />
      <CustomWarning
        open={pendingGroupNames.length > 0}
        onCancel={dismissGroupPrompt}
        onConfirm={openGroupManager}
        message={
          pendingGroupNames.length > 1 ? (
            MULTI_GROUP_PROMPT_MESSAGE
          ) : (
            <>
              YOU CREATED THE GROUP <span className={styles.promptGroupName}>{pendingGroupNames[0]}</span>. EDIT GROUP PERMISSIONS NOW?
            </>
          )
        }
        cancelText="NO"
        confirmText="YES"
        variant="dark"
        showIcon={false}
      />
    </FormProvider>
  );
};

export default UserFormModal;
